import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SharedActionBarComponent } from '../../shared-action-bar/shared-action-bar.component';
import { EventsService } from 'src/app/core-module/services/events.service';
import { EventTemplate } from 'src/app/core-module/models/Events/event-template';
import { EventTemplateLocation } from 'src/app/core-module/models/Events/event-template-location';
import { UpsertEventTemplateRequest } from 'src/app/core-module/models/Events/upsert-event-template-request';
import { UpsertEventTemplateResult } from 'src/app/core-module/models/Events/upsert-event-template-result';
import { FlattenedQuestionHolder } from 'src/app/core-module/models/Audits/flattened-question-holder';
import { Question } from 'src/app/core-module/models/Audits/question';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CustomerTermPipe, CustomerTextPipe } from 'src/app/shared-module/pipes/customer-term.pipe';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-event-template-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    ConfirmDialogModule,
    SharedActionBarComponent,
    CustomerTermPipe,
    CustomerTextPipe
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './event-template-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./event-template-detail.component.scss']
})
export class EventTemplateDetailComponent implements OnInit {
  private readonly customerContext = inject(CustomerContextService);
  templateId: number | null = null;
  templates: EventTemplate[] = [];
  locations: EventTemplateLocation[] = [];
  audits: FlattenedQuestionHolder[] = [];
  selectedAudit?: FlattenedQuestionHolder;
  editing: UpsertEventTemplateRequest = new UpsertEventTemplateRequest();
  locationSelection: number[] = [];
  loading = false;
  saving = false;
  hasUnsavedChanges = false;
  currentStep = 1;
  validationErrors: { [key: string]: string } = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.templateId = idParam && idParam !== 'new' ? Number(idParam) : null;
    this.loadData();
  }

  private loadData(): void {
    this.loading = true;
    this.eventsService.getEventTemplates().subscribe({
      next: res => {
        this.templates = res?.Templates ?? [];
        this.locations = res?.Locations ?? [];
        this.loadAudits();
        if (this.templateId) {
          const match = this.templates.find(t => t.EventWorkflowID === this.templateId);
          if (match) {
            this.applyTemplate(match);
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private loadAudits(): void {
    this.eventsService.getEventTemplateAudits().subscribe({
      next: audits => {
        this.audits = audits ?? [];
        this.syncSelectedAudit();
      },
      error: () => {
        this.audits = [];
      }
    });
  }

  private applyTemplate(t: EventTemplate): void {
    this.editing = {
      EventWorkflowID: t.EventWorkflowID,
      TemplateName: t.TemplateName || t.WorkflowName || '',
      MainAuditID: t.MainAuditID,
      LocationQuestionID: t.LocationQuestionID,
      DateQuestionID: t.DateQuestionID,
      TypeQuestionID: t.TypeQuestionID,
      SummaryQuestionID: t.SummaryQuestionID,
      RiskQuestionID: t.RiskQuestionID,
      Active: t.Active ?? true,
      LocationIDs: t.LocationIDs && t.LocationIDs.length > 0 ? [...t.LocationIDs] : undefined
    };
    this.locationSelection = t.LocationIDs && t.LocationIDs.length > 0 ? [...t.LocationIDs] : [];
    this.hasUnsavedChanges = false;
    this.currentStep = 1;
    this.syncSelectedAudit();
  }

  startNew(): void {
    this.templateId = null;
    this.editing = new UpsertEventTemplateRequest();
    this.editing.Active = true;
    this.locationSelection = [];
    this.hasUnsavedChanges = false;
    this.selectedAudit = undefined;
    this.currentStep = 1;
  }

  markDirty(field?: string): void {
    if (field && this.validationErrors[field]) {
      delete this.validationErrors[field];
    }
    this.hasUnsavedChanges = true;
  }

  get isValidTemplate(): boolean {
    const nameOk = !!this.editing.TemplateName && this.editing.TemplateName.trim().length > 0;
    const auditOk = !!this.editing.MainAuditID && this.editing.MainAuditID > 0;
    const questionsOk = [this.editing.LocationQuestionID, this.editing.DateQuestionID, this.editing.TypeQuestionID, this.editing.SummaryQuestionID, this.editing.RiskQuestionID].every(q => q !== undefined && q !== null && q > 0);
    return nameOk && auditOk && questionsOk;
  }

  private validateForm(): boolean {
    this.validationErrors = {};
    if (!this.editing.TemplateName || this.editing.TemplateName.trim().length === 0) {
      this.validationErrors.TemplateName = 'Template name is required';
    }
    if (!this.editing.MainAuditID || this.editing.MainAuditID <= 0) {
      this.validationErrors.MainAuditID = this.customerContext.displayText('Select the required audit');
    }
    const questionFields: { key: keyof UpsertEventTemplateRequest; label: string }[] = [
      { key: 'LocationQuestionID', label: 'Select a location question' },
      { key: 'DateQuestionID', label: 'Select a date question' },
      { key: 'TypeQuestionID', label: 'Select a type question' },
      { key: 'SummaryQuestionID', label: 'Select a summary question' },
      { key: 'RiskQuestionID', label: 'Select a risk question' }
    ];
    questionFields.forEach(f => {
      const val = this.editing[f.key] as number | undefined;
      if (!val || val <= 0) {
        this.validationErrors[f.key as string] = f.label;
      }
    });
    if (this.editing.MainAuditID && !this.selectedAudit) {
      this.validationErrors.MainAuditID = this.customerContext.displayText('Audit is not valid for this user');
    }
    return Object.keys(this.validationErrors).length === 0;
  }

  onAuditChange(): void {
    this.syncSelectedAudit();
    this.markDirty('MainAuditID');
    this.editing.LocationQuestionID = -1;
    this.editing.DateQuestionID = -1;
    this.editing.TypeQuestionID = -1;
    this.editing.SummaryQuestionID = -1;
    this.editing.RiskQuestionID = -1;
  }

  private syncSelectedAudit(): void {
    if (!this.editing?.MainAuditID) {
      this.selectedAudit = undefined;
      return;
    }
    this.selectedAudit = this.audits.find(a => a.AuditID === this.editing.MainAuditID);
  }

  questionOptions(): { label: string; value: number }[] {
    if (!this.selectedAudit) return [];
    return (this.selectedAudit.Questions ?? []).map((q: Question) => ({
      label: q.QuestionBody || q.QuestionHeader || `Question ${q.QuestionID}`,
      value: q.QuestionID
    }));
  }

  saveTemplate(): void {
    if (!this.validateForm()) {
      this.messageService.add({ severity: 'error', summary: 'Missing information', detail: 'Please complete all required fields.' });
      return;
    }
    this.saving = true;
    const payload: UpsertEventTemplateRequest = {
      ...this.editing,
      LocationIDs: this.locationSelection && this.locationSelection.length > 0 ? [...this.locationSelection] : undefined
    };
    this.eventsService.saveEventTemplate(payload).subscribe({
      next: (res: UpsertEventTemplateResult) => {
        this.saving = false;
        if (res?.IsError) {
          this.mapBackendValidation(res);
          this.messageService.add({ severity: 'error', summary: 'Save failed', detail: res.ErrorMessage || res.ErrorCode || 'Unable to save template' });
          return;
        }
        this.hasUnsavedChanges = false;
        this.router.navigate(['/settings/events/templates']);
      },
      error: () => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'An error occurred while saving' });
      }
    });
  }

  resetTemplate(): void {
    if (this.templateId) {
      const match = this.templates.find(t => t.EventWorkflowID === this.templateId);
      if (match) {
        this.applyTemplate(match);
      }
    } else {
      this.startNew();
    }
  }

  nextStep(): void {
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  back(): void {
    this.router.navigate(['/settings/events/templates']);
  }

  private mapBackendValidation(res: UpsertEventTemplateResult): void {
    this.validationErrors = {};
    switch (res?.ErrorCode) {
      case 'ETVAL_NAME':
        this.validationErrors.TemplateName = res.ErrorMessage || 'Template name is required';
        break;
      case 'ETVAL_AUDIT':
        this.validationErrors.MainAuditID = this.customerContext.displayText(res.ErrorMessage || 'Audit is invalid');
        break;
      case 'ETVAL_QUESTION':
        ['LocationQuestionID', 'DateQuestionID', 'TypeQuestionID', 'SummaryQuestionID', 'RiskQuestionID'].forEach(k => {
          this.validationErrors[k] = res.ErrorMessage || 'Question is required';
        });
        break;
      case 'ETVAL_LOCATION':
        this.validationErrors.LocationIDs = res.ErrorMessage || 'One or more locations are invalid';
        break;
      default:
        break;
    }
  }
}
